import { ANNOTATION_CONFIDENCE_THRESHOLD } from '../constants'
import type {
  AnnotationMetadata,
  HTMLAnchorFragment,
  SearchResult,
} from '../types'
import { DomLocatorService } from '../utils/DomLocatorService'
import { HighlightService } from '../utils/HighlightService'

import { AnnotationEngine } from './AnnotationEngine'

export class HTMLAnnotationEngine extends AnnotationEngine {
  private readonly domLocator: DomLocatorService
  private readonly highlightService: HighlightService

  constructor(root: Document | Element) {
    super()
    this.domLocator = new DomLocatorService(root)
    this.highlightService = new HighlightService(root)
  }

  getDomLocator(): DomLocatorService {
    return this.domLocator
  }

  get(rawData?: unknown): AnnotationMetadata | null {
    let range: Range

    if (rawData instanceof Range) {
      range = rawData
    } else {
      const selection = globalThis.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return null
      }
      range = selection.getRangeAt(0)
    }

    const selectedText = range.toString().trim()
    if (!selectedText) {
      return null
    }

    const { prefix, suffix } = this.domLocator.extractContext(range)

    const fragments: HTMLAnchorFragment[] = []
    const textNodes = this.domLocator.getTextNodesInRange(range)

    for (const textNode of textNodes) {
      const nodeRange = this.domLocator.getTextNodeRangeIntersection(
        range,
        textNode
      )
      if (!nodeRange) continue

      const path = this.domLocator.generateXPath(textNode)

      const fragmentText = nodeRange.toString()

      fragments.push({
        text: fragmentText,
        startPath: path,
        startOffset: nodeRange.startOffset,
        endPath: path,
        endOffset: nodeRange.endOffset,
      })
    }

    if (fragments.length === 0) {
      return null
    }

    return {
      text: selectedText,
      prefix,
      suffix,
      fragments,
    }
  }

  apply(annotation: AnnotationMetadata, commentId: string): boolean {
    const fragmentRanges = this.domLocator.applyViaFragments(annotation)

    if (fragmentRanges.length > 0) {
      let appliedAny = false
      fragmentRanges.forEach(({ range, index }) => {
        const ok = this.highlightService.wrapRange(range, commentId, index)
        if (ok) {
          appliedAny = true
        }
      })
      if (appliedAny) {
        return true
      }
    }

    const searchResult = this.search(annotation)

    if (!searchResult) {
      return false
    }

    return this.highlightService.wrapRange(searchResult.range, commentId, 0)
  }

  search(annotation: AnnotationMetadata): SearchResult | null {
    const xpathResult = this.domLocator.searchByXPath(annotation)
    if (xpathResult) {
      return {
        range: xpathResult,
        confidence: 1,
        method: 'xpath',
      }
    }

    const textMatches = this.domLocator.findRangesByText(annotation.text)

    if (textMatches.length === 0) {
      return null
    }

    if (textMatches.length === 1) {
      return {
        range: textMatches[0],
        confidence: 1,
        method: 'text-single',
      }
    }

    const bestMatch = this.domLocator.findBestMatchWithContext(
      textMatches,
      annotation.prefix,
      annotation.suffix
    )

    if (bestMatch && bestMatch.confidence >= ANNOTATION_CONFIDENCE_THRESHOLD) {
      return bestMatch
    }

    return null
  }

  remove(commentId: string): boolean {
    return this.highlightService.removeHighlight(commentId)
  }

  removeAll(): void {
    this.highlightService.removeAll()
  }
}
